/**
 * Currency utility functions
 */

export const SUPPORTED_CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "IDR", name: "Indonesian Rupiah", symbol: "Rp" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
  { code: "MYR", name: "Malaysian Ringgit", symbol: "RM" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
] as const;

export type CurrencyCode = typeof SUPPORTED_CURRENCIES[number]["code"];

/**
 * Format amount with currency
 */
export function formatCurrency(amount: number, currency: CurrencyCode = "IDR"): string {
  // Get locale based on currency
  const localeMap: Record<CurrencyCode, string> = {
    USD: "en-US",
    EUR: "de-DE",
    GBP: "en-GB",
    JPY: "ja-JP",
    IDR: "id-ID",
    SGD: "en-SG",
    MYR: "ms-MY",
    AUD: "en-AU",
    CAD: "en-CA",
    CNY: "zh-CN",
  };

  const locale = localeMap[currency] || "en-US";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: currency === "JPY" || currency === "IDR" ? 0 : 2,
    maximumFractionDigits: currency === "JPY" || currency === "IDR" ? 0 : 2,
  }).format(amount);
}

/**
 * Get currency info by code
 */
export function getCurrencyInfo(code: CurrencyCode) {
  return SUPPORTED_CURRENCIES.find((c) => c.code === code) || SUPPORTED_CURRENCIES[0];
}

/**
 * Validate currency code
 */
export function isValidCurrency(code: string): code is CurrencyCode {
  return SUPPORTED_CURRENCIES.some((c) => c.code === code);
}

